<template>
  <div>
      <div class="col s9">
        <div class="z-depth-1 white">
          <highcharts v-show="!isNoData && !isLoading" :options="chartOptions" ref="chart"></highcharts>
          <div v-show="isNoData && !isLoading">{{ $t('views.switch.noValuesForOil') }}</div>
          <div v-show="isLoading">{{ $t('loading') }}</div>
        </div>
      </div>
      <div class="col s3">
        <div class="col s12 z-depth-1 white" style="height: 400px;">
          <h5>Filter</h5>

          <div class="input-field">
            <date-picker @input="dateChanged" v-model="date" :max-date="maxDate" @dateChanged="datePickerChanged" style="padding-right: 6px;" />
            <period-selector v-model="selectedPeriod" />
          </div>
        </div>
      </div>
  </div>
</template>

<script>
import moment from 'moment'
import OilDigitalApi from '../../services/api/OilDigitalApi'
import RailObjectApi from '../../services/api/RailObjectsApi'
import SwitchApi from '../../services/api/switch/SwitchApi'
import RailObjectService from '../../services/RailObjectService'
import DatePicker from '@/components/switch/DatePicker'
import PeriodSelector from '@/components/PeriodSelector'
import Highcharts from 'highcharts'

export default {
  components: {
    'datePicker': DatePicker,
    'periodSelector': PeriodSelector
  },
  props: {
    switchObject: Object
  },
  watch: {
    switchObject () {
      this.loadSwitchObjects().then(() => {
        this.setAppData()
      })
    },

    selectedPeriod (period) {
      if (period === 'user_input') {
        return
      }
      const end = moment().startOf('day').format()
      const start = this.getStartDate(end)
      this.date = {
        start,
        end
      }
    }
  },
  data: function () {
    return {
      maxDate: new Date(),
      date: {
        start: moment().subtract(14, 'days').startOf('day').format(),
        end: moment().startOf('day').format()
      },
      selectedPeriod: '2weeks',
      relatedRailObjectName: '',

      isNoData: true,
      isNoRelatedData: true,
      isLoading: false,

      parentRailObject: undefined,

      chartOptions: {
        chart: {
          type: 'xrange',
          height: 400,
          zoomType: 'x',
          animation: false,
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        lang: {
          noData: this.$t('charts.nodata')
        },
        accessibility: { enabled: false },
        credits: { enabled: false },
        title: { text: '' },
        xAxis: {
          type: 'datetime',
          gridLineWidth: 1,
          lineColor: '#ccc',
          minRange: 1000
        },
        yAxis: {
          categories: ['Oil Level'],
          title: null,
          reversed: true,
          labels: { enabled: false }
        },
        tooltip: {
          formatter: function () {
            const start = this.point.x
            const end = this.point.x2
            const durationMs = end - start
            const minutes = Math.floor(durationMs / 60000)

            return `
              <b>${this.point.status}</b><br/>
              ${Highcharts.dateFormat('%e %b %Y %H:%M', start)}<br/>
              ${Highcharts.dateFormat('%e %b %Y %H:%M', end)}<br/>
              ${this.point.translatedDurationLabel}: ${minutes} ${this.point.translatedMinutesLabel}
            `
          }
        },
        series: [{
          name: this.$t('Switch.OilLevel.OilLevel'),
          data: [],
          pointWidth: 30,
          dataLabels: {
            enabled: true,
            format: '{point.status}',
            style: {
              fontWeight: 'bold',
              color: 'white'
            }
          }
        }]
      }
    }
  },
  mounted: async function () {
    await this.loadSwitchObjects()
    this.setAppData()
  },
  methods: {
    dateChanged: function () {
      this.setAppData()
    },

    loadSwitchObjects: async function () {
      this.relatedRailObjectName = this.switchObject.Name === 'Oil Low' ? 'Oil Low Low' : 'Oil Low'
      this.parentRailObject = await RailObjectApi.getRailObjectById(this.switchObject.ParentRailObjectId)
    },

    getStatus (oilLow, oilLowLow) {
      if (oilLow && !oilLowLow) return 'OK'
      if (!oilLow && !oilLowLow) return 'Low'
      if (!oilLow && oilLowLow) return 'Very Low'
      if (oilLow && oilLowLow) return 'Illegal'
    },

    getColor (status) {
      switch (status) {
        case 'OK': return '#28a745'
        case 'Low': return '#ffc107'
        case 'Very Low': return '#dc3545'
        case 'Illegal': return '#6f42c1'
        default: return '#6c757d'
      }
    },

    mergeOilSignalsToTimeline (signalOilLow, signalOilLowLow, dateEnd) {
      const allEvents = []

      for (const item of signalOilLow) {
        allEvents.push({ dt: new Date(item.dt).getTime(), source: 'OilLow', val: item.val === 'True' })
      }

      for (const item of signalOilLowLow) {
        allEvents.push({ dt: new Date(item.dt).getTime(), source: 'OilLowLow', val: item.val === 'True' })
      }

      allEvents.sort((a, b) => a.dt - b.dt)

      const timeline = []
      let currentOilLow = false
      let currentOilLowLow = false
      let prevStatus = null
      let intervalStart = allEvents.length > 0 ? allEvents[0].dt : null

      for (let i = 0; i < allEvents.length; i++) {
        const event = allEvents[i]
        let nextTime

        if (i + 1 < allEvents.length) {
          nextTime = allEvents[i + 1].dt
        } else {
          nextTime = new Date(dateEnd).getTime()
        }

        if (event.source === 'OilLow') currentOilLow = event.val
        if (event.source === 'OilLowLow') currentOilLowLow = event.val

        const newStatus = this.getStatus(currentOilLow, currentOilLowLow)

        if (prevStatus === null) {
          prevStatus = newStatus
          intervalStart = event.dt
        }

        const isLast = i === allEvents.length - 1
        const statusChanged = newStatus !== prevStatus

        if (statusChanged || isLast) {
          const intervalEnd = event.dt !== intervalStart ? event.dt : nextTime
          if (intervalStart !== intervalEnd) {
            timeline.push({
              x: intervalStart,
              x2: intervalEnd,
              y: 0,
              status: this.$t(`Switch.OilLevel.${prevStatus}`),
              color: this.getColor(prevStatus),
              translatedDurationLabel: this.$t('Switch.OilLevel.Duration'),
              translatedMinutesLabel: this.$t('Switch.OilLevel.Minutes')
            })
          }
          intervalStart = event.dt
          prevStatus = newStatus
        }
      }

      return timeline
    },

    async setAppData () {
      try {
        this.isLoading = true
        const start = moment(this.date.start).startOf('day')
        const end = moment(this.date.end).endOf('day')

        // We have switchObject and relatedSwitchObject, determine which is 'Oil Low' and which is 'Oil Low Low'
        const relatedSwitchObject = await this.getRelatedSwitchObject()
        const oilLowId = this.switchObject.Name.toLowerCase() === 'oil low' ? this.switchObject.UniqueId : relatedSwitchObject.UniqueId
        const oilLowLowId = this.switchObject.Name.toLowerCase() === 'oil low' ? relatedSwitchObject.UniqueId : this.switchObject.UniqueId

        // Gather oil low data
        const oilLowData = await OilDigitalApi.getRelayMeasurements({
          pointName: this.parentRailObject.UniqueId,
          relayName: oilLowId,
          relayType: this.switchObject.EventCode,
          numberOfDays: start.utc().format() + '-til-' + end.utc().format()
        })

        // Gather oil low low data
        const oilLowLowData = await OilDigitalApi.getRelayMeasurements({
          pointName: this.parentRailObject.UniqueId,
          relayName: oilLowLowId,
          relayType: relatedSwitchObject.EventCode,
          numberOfDays: start.utc().format() + '-til-' + end.utc().format()
        })

        // Combine both signals into a single oil status timeline
        const timelineData = this.mergeOilSignalsToTimeline(
          oilLowData.data,
          oilLowLowData.data,
          end
        )

        this.chartOptions.series[0].data = timelineData
        this.isNoData = timelineData.length === 0
      } catch (error) {
        this.isNoData = true
      } finally {
        this.isLoading = false
      }
    },

    getRelatedSwitchObject: async function () {
      const children = RailObjectService.getRailObjectChildrenByParentId(this.switchObject.ParentRailObjectId)
      const relatedRailObject = children.find(c => c.Name === this.relatedRailObjectName)

      const switchObjectData = await SwitchApi.getSwitch(relatedRailObject.RailObjectId)
      return switchObjectData.data
    },

    getStartDate (endDate) {
      switch (this.selectedPeriod) {
        case '1week':
          return moment(endDate).subtract(1, 'weeks')
        case '2weeks':
          return moment(endDate).subtract(2, 'weeks')
        case '1month':
          return moment(endDate).subtract(1, 'months')
        case '3months':
          return moment(endDate).subtract(3, 'months')
        case '6months':
          return moment(endDate).subtract(6, 'months')
        case '1year':
          return moment(endDate).subtract(1, 'years')
        default:
          return moment(endDate).subtract(1, 'months')
      }
    },

    datePickerChanged () {
      this.selectedPeriod = 'user_input'
    }
  }
}
</script>

<style scoped>
.popover-container {
  display: inline-block;
}

.checkbox-label {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}
</style>



// WEBPACK FOOTER //
// src/components/switch/OilDigital.vue